import React, { useEffect, useRef, useState } from "react";
import { useAppSelector } from "../../../../store/hooks/StoreHooks";
import useCollapseFacade from "../../../../facade/collapse.Facade";
import Accordion from "../../../common/accordion";
import ButtonGroup from "../../../common/button-group";
import SwitchButton from "../../../common/switch-button";
import TableComponent from "../../../common/TableComponent";
import { surveyColumns } from "../../../../constants/columns/calculations/wellSurvey";
import Textarea from "../../../common/textarea";
import LABELS from "../../../../constants/labels/wellsurvey.labels";
import RadioButton from "../../../common/radio-button";
import TextInput from "../../../common/textinput";
import * as XLSX from 'xlsx';
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../store/ConfigureStore";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import WellSurveyPlot1 from "../WellSurveyPlot1";
import WellSurveyPlot2 from "../WellSurveyPlot2";
import useWellSurveyFacade from "../../../../facade/calculation/calculation-output/well-survey.facade";
import "./styles.scss";

import { setNotes } from "../../../../store/calculation/calculation-output/wellSurveySlice";
import html2canvas from "html2canvas";
import { HelpCircle } from "../../../../assets/icons";





const PerformanceCurves: React.FC = () => {
  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | number | null>(null);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const { schematicData } = useAppSelector(state => state?.calculations);
  const equipmentForBelow = useAppSelector((state: RootState) => state.schematic.ESPEquipmentsForBelow);
  const lengthValue = equipmentForBelow?.[0]?.length ?? 0;
  const tailPipeBottom = useAppSelector((state: RootState) => state.wellSurvey.TailPipeBottom ?? 0);
  const tailPipeLength = useAppSelector((state: RootState) => state.wellSurvey.TailPipeLength ?? 0);
  const includeInMainReport = useAppSelector((state: RootState) => state?.wellSurvey?.showWellDataInMainReport ?? false);
  const { customerEnabled, customerCreated } = useAppSelector((state) => state.auth);
  const { projectReadOnly } = useAppSelector((state) => state.projectHeader);
  const surveyChartRef = useRef<HTMLDivElement | null>(null);
  const surveyChart2Ref = useRef<HTMLDivElement | null>(null);
  const surveyMetaData = {
    kop: useAppSelector((state: RootState) => state.wellSurvey.KOPOverride ?? 0),
    topOfEspPump: ((schematicData?.sensorDepth ?? 0) - (schematicData?.totalEspLength ?? 0)),
    bottomOfESPSensor: schematicData?.sensorDepth ?? 0,
    tailPipeDesander: ((lengthValue ?? 0) + (tailPipeLength ?? 0)),
    tailPipeBottom: tailPipeBottom ?? 0
  };
  const tableRef = useRef<HTMLTableElement>(null);
  const { loading, tab1Input } = useAppSelector((state) => state?.tubing);


  const { performanceCurvesCollapse } = useAppSelector(
    (state) => state?.collapse
  );
  const { updateKOPOverride, updateMaxDLSAboveKOP, updateWellDataInMainReportFlag, updateWellNotes, clearWellSurvey } = useWellSurveyFacade();
  const { updatePerformanceCurvesCollapse } = useCollapseFacade();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const KOPOverride = useAppSelector((state: RootState) => state.wellSurvey.KOPOverride ?? 0);
  const MaxDLSAboveKOP = useAppSelector((state: RootState) => state.wellSurvey.MaxDLSAboveKOP ?? 0);
  const depthOfMaxDLS = useAppSelector((state: RootState) => state.wellSurvey.DepthOfMaxDLS ?? 0);
  const { updateTransitionZone } = useWellSurveyFacade();
  const { updateTailPipeLength } = useWellSurveyFacade();
  const { updateTailPipeBottom } = useWellSurveyFacade();
  const { updateTailPipeInsideTZ } = useWellSurveyFacade();
  const tailPipeTZ = useAppSelector((state: RootState) => state.wellSurvey.TailPipeInsideTZ ?? 0);
  const [KOPOverrideValue, setKOPOverrideValue] = useState<number>(KOPOverride);
  const [MaxDLSAboveKOPValue, setMaxDLSAboveKOPValue] = useState<number>(MaxDLSAboveKOP);
  const [depthOfMaxDLSValue, setDepthOfMaxDLSValue] = useState<number>(depthOfMaxDLS);
  const notes = useAppSelector((state: RootState) => state.wellSurvey.wellSurvey.notes);
  const selectedProject = useAppSelector((state: RootState) => state.myProjects.selectedProject);
  const wellSurveyData = useSelector((state: RootState) => state.wellSurvey.wellSurveyData);
  const { isWellSurveyDataPersisted, loading: wellSurveyLoading } = useAppSelector(state => state.wellSurvey);

  useEffect(() => {
    setKOPOverrideValue(KOPOverride);
    setMaxDLSAboveKOPValue(MaxDLSAboveKOP);
    setDepthOfMaxDLSValue(depthOfMaxDLS);
  }, [KOPOverride, MaxDLSAboveKOP, depthOfMaxDLS]);

  // Reset component state when well survey data is cleared
  useEffect(() => {
    if (!wellSurveyData || wellSurveyData.length === 0) {
      setSelectedRow(null);
      setSelectedRowId(null);
      setHoveredRowIndex(null);
    }
  }, [wellSurveyData]);

  // Clear form after successful import
  useEffect(() => {
    if (wellSurveyData && wellSurveyData.length > 0 && !wellSurveyLoading) {
      setInputValues({
        startCell: { md: '', inc: '', azi: '' },
        endCell: { md: '', inc: '', azi: '' },
        sheetName: ''
      });
      setSelectedFile(null);
      setErrors({
        startCell: { md: false, inc: false, azi: false },
        endCell: { md: false, inc: false, azi: false },
        sheetName: false
      });
    }
  }, [wellSurveyData, wellSurveyLoading]);
  const [inputValues, setInputValues] = useState({
    startCell: {
      md: '',
      inc: '',
      azi: ''
    },
    endCell: {
      md: '',
      inc: '',
      azi: ''
    },
    sheetName: '' // Add this new field
  });
  // Add this with other state declarations at the top of the component
  const [isAzi, setIsAzi] = useState<boolean>(true); // true for Azi, false for DLS
  // dispatch is now declared at the top of the component


  const [errors, setErrors] = useState({
    startCell: {
      md: false,
      inc: false,
      azi: false
    },
    endCell: {
      md: false,
      inc: false,
      azi: false
    },
    sheetName: false // Add this new field
  });


  const maxDls = wellSurveyData ? Math.max(...wellSurveyData.map((item: any) => item.dls || 0)) : 0;
  const maxMd = Math.max(...wellSurveyData.map((item: any) => item.md));
  const transitionZone = useAppSelector((state: RootState) => state.wellSurvey.TransitionZone ?? 0);
  const {
    projectName,
    caseName,
    contact,
    country,
    wellApiNo,
    represtative,
    equiment,
    formation,
    county,
    countyOther,
    customer,
    state,
    well,
    unitMeasurement
  } = useAppSelector((state) => state?.createProject);

  const handleRadioChange = (value: boolean) => {
    setIsAzi(value);
  };
  const dispatch = useDispatch();

  const handleInputChange = (
    section: 'startCell' | 'endCell' | 'sheetName',
    value: string,
    field?: 'md' | 'inc' | 'azi'
  ) => {
    if (section === 'sheetName') {
      setInputValues(prev => ({
        ...prev,
        sheetName: value
      }));
      setErrors(prev => ({
        ...prev,
        sheetName: false
      }));
    } else if (field) {
      setInputValues(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
      setErrors(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: false
        }
      }));
    }
  };

  const handleCollapseChange = (key: boolean) => {
    updatePerformanceCurvesCollapse(key);
  };
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
      if (file) {
        setSelectedFile(file);
      } else {
        alert('Please select any file');
      }
      event.target.value = '';
  };
  const handleRowClick = (row: any) => {
    setSelectedRow(row);
    setSelectedRowId(row.id); // Make sure the row has an id property
    updateKOPOverride(row.md);
  };

  const handleRowHover = (rowIndex: number | null) => {
    setHoveredRowIndex(rowIndex); // Save hovered row index
  };

  const extractExcelData = async (file: File, startCell: any, endCell: any, sheetName: string) => {
    try {
      // Read the file
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);

      // Validate sheet name
      if (!workbook.SheetNames.includes(sheetName)) {
        throw new Error(`Sheet name "${sheetName}" not found. Available sheets: ${workbook.SheetNames.join(', ')}`);
      }

      const worksheet = workbook.Sheets[sheetName];

      // Convert cell references (like "A1") to indices
      const startCellRef = {
        md: XLSX.utils.decode_cell(startCell.md),
        inc: XLSX.utils.decode_cell(startCell.inc),
        azi: XLSX.utils.decode_cell(startCell.azi)
      };

      const endCellRef = {
        md: XLSX.utils.decode_cell(endCell.md),
        inc: XLSX.utils.decode_cell(endCell.inc),
        azi: XLSX.utils.decode_cell(endCell.azi)
      };

      // Extract data for each column
      const extractColumn = (startRef: XLSX.CellAddress, endRef: XLSX.CellAddress) => {
        const data = [];
        for (let row = startRef.r; row <= endRef.r; row++) {
          const cellRef = XLSX.utils.encode_cell({ r: row, c: startRef.c });
          const cell = worksheet[cellRef];
          data.push(cell ? cell.v : null);
        }
        return data;
      };

      // Extract data for each column
      const extractedData = {
        md: extractColumn(startCellRef.md, endCellRef.md),
        inc: extractColumn(startCellRef.inc, endCellRef.inc),
        azi: extractColumn(startCellRef.azi, endCellRef.azi)
      };

      return extractedData;
    } catch (error) {
      console.error('Error reading Excel file:', error);
      throw error;
    }
  };

  const handleImport = () => {
    // Add validation first
    const newErrors = {
      startCell: {
        md: inputValues.startCell.md.trim() === '',
        inc: inputValues.startCell.inc.trim() === '',
        azi: inputValues.startCell.azi.trim() === ''
      },
      endCell: {
        md: inputValues.endCell.md.trim() === '',
        inc: inputValues.endCell.inc.trim() === '',
        azi: inputValues.endCell.azi.trim() === ''
      },
      sheetName: inputValues.sheetName.trim() === '',
      file: !selectedFile
    };

    setErrors(newErrors);

    // Check if any errors exist
    const hasErrors = Object.values(newErrors.startCell).some(Boolean) ||
      Object.values(newErrors.endCell).some(Boolean) ||
      newErrors.sheetName ||
      newErrors.file;

    if (hasErrors) {
      console.error("Validation failed");
      return;
    }

    // If validation passes, proceed with form submission
    try {
      const formData = new FormData();

      if (selectedFile) {
        formData.append('ServeyFile', selectedFile);
      }

      formData.append('SheetName', inputValues.sheetName);
      formData.append('MD.StartCell', inputValues.startCell.md);
      formData.append('MD.EndCell', inputValues.endCell.md);
      formData.append('INC.StartCell', inputValues.startCell.inc);
      formData.append('INC.EndCell', inputValues.endCell.inc);
      formData.append('IsAZI', isAzi.toString()); // This will be 'true' or 'false'
      formData.append('AziOrDLS.StartCell', inputValues.startCell.azi);
      formData.append('AziOrDLS.EndCell', inputValues.endCell.azi);
      dispatch({
        type: "wellSurvey/fetchData",
        payload: { inputData: formData }
      });
    } catch (error) {
      console.error("Error during import:", error);
    }
  };

  const clearData = () => {
    try {
      clearWellSurvey();
      setInputValues({
        startCell: { md: '', inc: '', azi: '' },
        endCell: { md: '', inc: '', azi: '' },
        sheetName: ''
      });
      setSelectedFile(null);
      setErrors({
        startCell: { md: false, inc: false, azi: false },
        endCell: { md: false, inc: false, azi: false },
        sheetName: false
      });
    } catch (error) {
      console.error('Error clearing data:', error);
    }
  }
   
  const handleToggleButton = (checked: boolean) => {
    if (checked) {
      updateWellDataInMainReportFlag(true);
    }
    else {
      updateWellDataInMainReportFlag(false);
    }
  }
  const exportToExcel = () => {
    try {
      // Extract data from wellSurveyData
      const exportData = wellSurveyData.map((row: { md: number; inc: number; dls: number }) => ({
        MD: row.md,
        INC: row.inc,
        DLS: row.dls
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Survey Data');

      // Generate Excel file
      XLSX.writeFile(wb, 'WellSurveyData.xlsx');

    } catch (error) {
      console.error('Error exporting to Excel:', error);
    }
  };
  const handleWellSurveyChart1Download = () => {
    if (!surveyChartRef.current) {
      return;
    }
    if (surveyChartRef.current) {
      html2canvas(surveyChartRef.current, {
        backgroundColor: "#253040",
      }).then((canvas) => {
        const imgUrl = canvas.toDataURL("image/png");

        const link = document.createElement("a");
        link.href = imgUrl;
        link.download = "Survey Performance Plots.png";
        link.click();
      });
    }
  };

  const handleWellSurveyChart2Download = () => {
    if (!surveyChart2Ref.current) {
      return;
    }
    if (surveyChart2Ref.current) {
      html2canvas(surveyChart2Ref.current, {
        backgroundColor: "#253040",
      }).then((canvas) => {
        const imgUrl = canvas.toDataURL("image/png");

        const link = document.createElement("a");
        link.href = imgUrl;
        link.download = "Survey Performance Plots.png";
        link.click();
      });
    }
  };

  const handleWellNotesChange = (event: any) => {
    updateWellNotes(event.target.value);
  }


  const template = useAppSelector((state: RootState) => state.createProject.template);


  // Add a loading state for initial data fetch
  const [isLoading, setIsLoading] = useState(false);


  return (
    <div>
      <ToastContainer />
      <Accordion
        title={LABELS?.performanceCurves}
        isOpen={performanceCurvesCollapse}
        onToggle={() => handleCollapseChange(!performanceCurvesCollapse)}
        content={
          <div className='flex flex-col gap-[24px] p-4 w-full h-full'>
            {((!customerEnabled && !projectReadOnly) || (customerEnabled && customerCreated && !projectReadOnly)) && (
              <>
                <div className='flex justify-between items-center'>
                  <p className='text-neutral-25'>{LABELS?.importFromExcel}</p>
                  <ButtonGroup
                    buttonOptions={[
                      {
                        id: 1,
                        icon: "",
                        label: `${LABELS?.btnImport}`,
                        type: `${LABELS?.typePrimary}`,
                      },
                    ]}
                    handleClick={handleImport}
                  />
                </div>
                <div className='relative border-[1px] rounded-[8px] border-darkcolor-400'>
                  <table className='table-fixed w-full rounded-[8px] outline-[1px]'>
                    <thead style={{ background: "#253040" }}>
                      <tr className='border-b-[1px] border-darkcolor-400'>
                        <th className='w-1/4 pl-[16px] pr-[12px] py-[16px] text-[12px] text-neutral-25 text-left'>
                          {LABELS?.excelInputs}
                        </th>
                        <th className='w-1/4 pr-[12px] py-[16px] text-[12px] text-neutral-25 text-left'>
                          {LABELS?.md}
                        </th>
                        <th className='w-1/4 pr-[12px] py-[16px] text-[12px] text-neutral-25 text-left'>
                          {LABELS?.inc}
                        </th>
                        <th className='w-1/4 pr-[12px] py-[16px] text-[12px] text-neutral-25 text-left'>
                          <div className='flex gap-[16px]'>
                            <RadioButton
                              label={LABELS?.azi}
                              checked={isAzi}
                              onChange={() => handleRadioChange(true)}
                            />
                            <RadioButton
                              label={LABELS?.dls}
                              checked={!isAzi}
                              onChange={() => handleRadioChange(false)}
                            />
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className='border-b-[1px] border-darkcolor-400'>
                        <td className='w-1/4 px-[24px] py-[9px] text-[14px] text-neutral-25'>
                          {LABELS?.startCellLocation}
                        </td>
                        <td className='w-1/4 pr-[12px] py-[9px]'>
                          <TextInput
                            placeholder=''
                            value={inputValues.startCell.md}
                            onChange={(e) =>
                              handleInputChange(
                                "startCell",
                                e.target.value,
                                "md"
                              )
                            }
                            bgColor='bg-darkcolor-800'
                            error={errors.startCell.md ? " " : ""}
                          />
                        </td>
                        <td className='w-1/4 pr-[12px] py-[9px]'>
                          <TextInput
                            placeholder=''
                            value={inputValues.startCell.inc}
                            onChange={(e) =>
                              handleInputChange(
                                "startCell",
                                e.target.value,
                                "inc"
                              )
                            }
                            bgColor='bg-darkcolor-800'
                            error={errors.startCell.inc ? " " : ""}
                          />
                        </td>
                        <td className='w-1/4 pr-[12px] py-[9px]'>
                          <TextInput
                            placeholder=''
                            value={inputValues.startCell.azi}
                            onChange={(e) =>
                              handleInputChange(
                                "startCell",
                                e.target.value,
                                "azi"
                              )
                            }
                            bgColor='bg-darkcolor-800'
                            error={errors.startCell.azi ? " " : ""}
                          />
                        </td>
                      </tr>
                      <tr className='border-b-[1px] border-darkcolor-400'>
                        <td className='w-1/4 px-[24px] py-[9px] text-[14px] text-neutral-25'>
                          {LABELS?.endCellLocation}
                        </td>
                        <td className='w-1/4 pr-[12px] py-[9px]'>
                          <TextInput
                            placeholder=''
                            value={inputValues.endCell.md}
                            onChange={(e) =>
                              handleInputChange(
                                "endCell",
                                e.target.value,
                                "md"
                              )
                            }
                            bgColor='bg-darkcolor-800'
                            error={errors.endCell.md ? " " : ""}
                          />
                        </td>
                        <td className='w-1/4 pr-[12px] py-[9px]'>
                          <TextInput
                            placeholder=''
                            value={inputValues.endCell.inc}
                            onChange={(e) =>
                              handleInputChange(
                                "endCell",
                                e.target.value,
                                "inc"
                              )
                            }
                            bgColor='bg-darkcolor-800'
                            error={errors.endCell.inc ? " " : ""}
                          />
                        </td>
                        <td className='w-1/4 pr-[12px] py-[9px]'>
                          <TextInput
                            placeholder=''
                            value={inputValues.endCell.azi}
                            onChange={(e) =>
                              handleInputChange(
                                "endCell",
                                e.target.value,
                                "azi"
                              )
                            }
                            bgColor='bg-darkcolor-800'
                            error={errors.endCell.azi ? " " : ""}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td className='w-1/4 px-[24px] py-[9px] text-[14px] text-neutral-25'>
                          {LABELS?.sheetName}
                        </td>
                        <td className='w-1/4 pr-[12px] py-[9px]'>
                          <TextInput
                            placeholder=''
                            value={inputValues.sheetName}
                            onChange={(e) =>
                              handleInputChange("sheetName", e.target.value)
                            }
                            bgColor='bg-darkcolor-800'
                            error={errors.sheetName ? " " : ""}
                          />
                        </td>
                        <td colSpan={2} className='w-2/4 pr-[12px] py-[9px]'>
                          <label
                            htmlFor='file-upload'
                            className='cursor-pointer bg-darkcolor-800 flex items-center w-full rounded-[8px] 
                      border-[1px] border-darkcolor-400 hover:border-primary-500 
                      focus:ring-primary-500 focus-focus-within:border-primary-500 
                      px-4 py-2'
                          >
                            <input
                              id='file-upload'
                              type='file'
                              accept='.xlsx,.xls'
                              onChange={handleFileChange}
                              className='hidden'
                            />
                            <span className='flex-1 text-white truncate'>
                              {selectedFile
                                ? selectedFile.name
                                : "No file selected"}
                            </span>
                            <span
                              className='ml-4 px-[10px] py-[4px] rounded-[8px] 
                            border-[1px] border-darkcolor-400 text-sm font-normal 
                            hover:bg-darkcolor-500 hover:border-darkcolor-300 
                            font-bold text-base text-white'
                            >
                              Choose file
                            </span>
                          </label>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {(isLoading || wellSurveyLoading) ? (
              <div className='flex justify-center items-center p-4'>
                <span className='text-neutral-25'>Loading...</span>
              </div>
            ) : (
              <>
                {wellSurveyData && wellSurveyData.length > 0 ? (
                  <>
                    <div className='flex justify-between items-center mb-4'>
                      <p className='text-neutral-25'>Survey data</p>
                      <div className='flex gap-[8px]'>
                        <SwitchButton
                          label={LABELS?.includeInMainReport}
                          checked={includeInMainReport}
                          onChange={handleToggleButton}
                          disabled={false}
                        />
                        <ButtonGroup
                          buttonOptions={[
                            {
                              id: 1,
                              icon: "",
                              label: `${LABELS?.exportToExcel}`,
                              type: "",
                            },
                          ]}
                          handleClick={exportToExcel}
                        />
                        {
                          ((customerEnabled && customerCreated && !projectReadOnly) || (!customerEnabled && !projectReadOnly)) &&
                          <ButtonGroup
                            buttonOptions={[
                              {
                                id: 1,
                                icon: "",
                                label: `${LABELS?.clearData}`,
                                type: "",
                                tooltip: isWellSurveyDataPersisted 
                                  ? "This will delete data from database" 
                                  : "This will clear imported data"
                              },
                            ]}
                            handleClick={clearData}
                        />
                        }
                      
                      </div>
                    </div>

                    <div className='well-survey'>
                      <TableComponent
                        name={"wellSurveyList"}
                        data={wellSurveyData}
                        columns={surveyColumns}
                        rowsPerPage={100}
                        clickableRows={true}
                        onRowClick={handleRowClick}
                        hiddenPumpId={
                          surveyMetaData?.kop
                            ? surveyMetaData?.kop
                            : undefined
                        }
                        overFlowClass='max-h-[400px] overflow-auto'
                      />
                    </div>

                    <div className='flex text-neutral-25 mt-4'>
                      {LABELS?.espSettingDepthSUrvey}
                      <a
                        className='ml-1 cursor-pointer'
                        href='/help?tab=Well-Survey#espAssembly'
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        <HelpCircle className='cursor-pointer' />
                      </a>
                    </div>
                    <div className='flex justify-between items-center border-[1px] border-darkcolor-400 rounded-[8px] w-full mb-8'>
                      <div className='flex flex-col items-center w-1/2'>
                        <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                          <label className='text-neutral-25 text-[14px] font-normal'>
                            {LABELS?.kop}
                          </label>
                          <label className='flex justify-end flex-wrap text-neutral-25'>
                            {KOPOverride}
                          </label>
                        </div>
                        <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                          <label className='text-neutral-25'>
                            {LABELS?.topESPPump}
                          </label>
                          <label className='flex justify-end flex-wrap text-neutral-25'>
                            {schematicData?.eoTubing}
                          </label>
                        </div>
                        <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                          <label className='text-neutral-25'>
                            {LABELS?.bottomESPSensor}
                          </label>
                          <label className='flex justify-end flex-wrap text-neutral-25'>
                            {schematicData?.sensorDepth}
                          </label>
                        </div>
                        <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                          <label className='text-neutral-25'>
                            {LABELS?.maxDLSaboveKop}
                          </label>
                          <label className='flex justify-end flex-wrap text-neutral-25'>
                            {MaxDLSAboveKOP}
                          </label>
                        </div>
                        <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                          <label className='text-neutral-25'>
                            {LABELS?.depthOfMaxDLS}
                          </label>
                          <label className='flex justify-end flex-wrap text-neutral-25'>
                            {depthOfMaxDLS}
                          </label>
                        </div>
                        <div className='flex justify-between items-center py-[9px] px-[24px] w-full'>
                          <label className='text-neutral-25'>
                            {LABELS?.casingSize}
                          </label>
                          <label className='flex justify-end flex-wrap text-neutral-25'>
                            {tab1Input?.casingODList?.filter(
                              (item: any) =>
                                +item?.value === tab1Input?.casingOD
                            )?.[0]?.label || ""}{" "}
                            &nbsp;
                            {tab1Input?.casingWeightList?.filter(
                              (item: any) =>
                                +item?.value === tab1Input?.casingWeight
                            )?.[0]?.label || ""}{" "}
                            &nbsp;
                          </label>
                        </div>
                      </div>
                      <div className='flex flex-col items w-1/2'>
                        <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                          <label className='text-neutral-25 text-[14px] font-normal'>
                            {LABELS?.transitionZone}
                          </label>
                          <label className='flex justify-end flex-wrap text-neutral-25'>
                            {transitionZone}
                          </label>
                        </div>
                        <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                          <label className='text-neutral-25'>
                            {LABELS?.espLength}
                          </label>
                          <label className='flex justify-end flex-wrap text-neutral-25'>
                            {schematicData?.totalEspLength ?? 0}
                          </label>
                        </div>
                        <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                          <label className='text-neutral-25'>
                            {LABELS?.tailPipeDesander}
                          </label>
                          <label className='flex justify-end flex-wrap text-neutral-25'>
                            {(tailPipeLength ?? 0).toFixed(2)}
                          </label>
                        </div>
                        <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                          <label className='text-neutral-25'>
                            {LABELS?.tailPipeBottom}
                          </label>
                          <label className='flex justify-end flex-wrap text-neutral-25'>
                            {tailPipeBottom}
                          </label>
                        </div>
                        <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                          <label className='text-neutral-25'>
                            {LABELS?.tailPipeInsideTZ}
                          </label>
                          <label className='flex justify-end flex-wrap text-neutral-25'>
                            {tailPipeTZ}
                          </label>
                        </div>
                        <div className='flex justify-between items-center py-[9px] px-[24px] w-full'>
                          <label className='text-neutral-25'>
                            {LABELS?.tubingSize}
                          </label>
                          <label className='flex justify-end flex-wrap text-neutral-25'>
                            {tab1Input?.tubingODList?.filter(
                              (item: any) =>
                                +item?.value === tab1Input?.tubingOD
                            )?.[0]?.label || ""}{" "}
                            &nbsp;
                            {tab1Input?.tubingWeightList?.filter(
                              (item: any) =>
                                +item?.value === tab1Input?.tubingWeight
                            )?.[0]?.label || ""}{" "}
                            &nbsp;
                          </label>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className='flex justify-center items-center p-4'>
                    <span className='text-neutral-25'>
                      No survey data available.
                    </span>
                  </div>
                )}

                {wellSurveyData?.length > 0 && surveyMetaData?.kop && (
                  <div className='flex flex-col w-full gap-8'>

                    <div className='flex flex-col w-full'>
                      <div className='flex text-neutral-25 mb-4'>
                        {LABELS?.wellsurveyplot1}
                      </div>
                      <div
                        className='text-neutral-25 font-bold'
                        style={{ width: "100%" }}
                      >
                        <button
                          onClick={handleWellSurveyChart1Download}
                          className='ml-[16px] mb-4 border-[1px] border-solid border-[#4A5463] py-[8px] px-[14px] inline-flex justify-center items-center gap-[8px] rounded-[8px] bg-transparent text-neutral-25 float-right'
                        >
                          Export
                        </button>
                        <div
                          className='flex justify-center items-start w-full'
                          ref={surveyChartRef}
                        >
                          <WellSurveyPlot1
                            selectedRow={selectedRow}
                            surveyData={wellSurveyData}
                            maxDls={maxDls}
                            maxMd={maxMd}
                            customerName={customer?.toString()}
                            wellName={well?.toString()}
                            surveyMetaData={surveyMetaData}
                            schematicData={
                              schematicData
                                ? {
                                  eoTubing: schematicData.eoTubing ?? 0,
                                  sensorDepth:
                                    schematicData.sensorDepth ?? 0,
                                }
                                : null
                            }
                          />
                        </div>
                      </div>
                    </div>
                    <div className='w-full my-8'>
                      <Textarea
                        label={LABELS?.notes}
                        value={notes ?? ""}
                        maxLength={1000}
                        onChange={(e) => handleWellNotesChange(e)}
                        hasOverflow = {true}
                      />
                    </div>
                    <div className='flex flex-col w-full'>
                      <div className='flex text-neutral-25 mb-4'>
                        {LABELS?.wellsurveyplot2}
                      </div>
                      <div className='text-neutral-25 font-bold'>
                        <button
                          onClick={handleWellSurveyChart2Download}
                          className='ml-[16px] mb-4 border-[1px] border-solid border-[#4A5463] py-[8px] px-[14px] inline-flex justify-center items-center gap-[8px] rounded-[8px] bg-transparent text-neutral-25 whitespace-nowrap min-w-max float-right'
                        >
                          Export
                        </button>
                        <div
                          className='flex justify-center items-start w-full'
                          ref={surveyChart2Ref}
                        >
                          <WellSurveyPlot2
                            selectedRow={selectedRow}
                            surveyData={wellSurveyData}
                            maxDls={maxDls}
                            maxMd={maxMd}
                            customerName={customer?.toString()}
                            wellName={well?.toString()}
                            surveyMetaData={surveyMetaData}
                            schematicData={
                              schematicData
                                ? {
                                  eoTubing: schematicData.eoTubing ?? 0,
                                  sensorDepth:
                                    schematicData.sensorDepth ?? 0,
                                }
                                : null
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        }
      />
    </div>
  );
};

interface TableProps {
  name?: string;
  data: any[];
  columns: any[];
  rowsPerPage: number;
  clickableRows?: boolean;
  onRowClick?: (row: any) => void;
  hiddenPumpId?: string | number;
  hiddenMotorId?: string | number;
  overFlowClass?: string;
}

export default PerformanceCurves;